export type RouteTransitionKind = 'forward' | 'back' | 'lateral' | 'none';

export type RouteNavigationType = 'POP' | 'PUSH' | 'REPLACE';

export interface RouteTransitionLocationState {
  routeTransition?: RouteTransitionKind;
}

type RouteFamily = {
  parent: string;
  isChild: (pathname: string) => boolean;
};

const ROUTE_FAMILIES: RouteFamily[] = [
  {
    parent: '/documents',
    isChild: (pathname) =>
      pathname.startsWith('/document-editor/') ||
      (pathname.startsWith('/documents/') && pathname !== '/documents'),
  },
  {
    parent: '/work-items',
    isChild: (pathname) => pathname.startsWith('/work-item-detail/'),
  },
  {
    parent: '/settings',
    isChild: (pathname) =>
      pathname.startsWith('/settings/') && pathname !== '/settings',
  },
];

function parseDocumentDetailId(pathname: string): string | null {
  const editorMatch = pathname.match(/^\/document-editor\/([^/]+)$/);
  if (editorMatch) return editorMatch[1];
  const documentsMatch = pathname.match(/^\/documents\/([^/]+)$/);
  if (documentsMatch) return documentsMatch[1];
  return null;
}

function parseWorkItemDetailId(pathname: string): string | null {
  const match = pathname.match(/^\/work-item-detail\/([^/]+)$/);
  return match ? match[1] : null;
}

function isLateralTransition(from: string, to: string): boolean {
  const fromDoc = parseDocumentDetailId(from);
  const toDoc = parseDocumentDetailId(to);
  if (fromDoc && toDoc && fromDoc !== toDoc) return true;

  const fromWorkItem = parseWorkItemDetailId(from);
  const toWorkItem = parseWorkItemDetailId(to);
  if (fromWorkItem && toWorkItem && fromWorkItem !== toWorkItem) return true;

  return false;
}

function isParentPath(pathname: string, family: RouteFamily): boolean {
  return pathname === family.parent;
}

export function classifyRouteTransition(
  from: string,
  to: string,
  navType: RouteNavigationType,
  state?: RouteTransitionLocationState | null
): RouteTransitionKind {
  if (from === to) return 'none';

  const explicit = state?.routeTransition;
  if (explicit && explicit !== 'none') return explicit;

  if (isLateralTransition(from, to)) return 'lateral';

  for (const family of ROUTE_FAMILIES) {
    const fromParent = isParentPath(from, family);
    const toParent = isParentPath(to, family);
    const fromChild = family.isChild(from);
    const toChild = family.isChild(to);

    if (fromParent && toChild) return 'forward';
    if (fromChild && toParent) return 'back';
  }

  if (navType === 'POP') {
    for (const family of ROUTE_FAMILIES) {
      if (family.isChild(from) && isParentPath(to, family)) return 'back';
    }
  }

  if (navType === 'PUSH' || navType === 'REPLACE') {
    for (const family of ROUTE_FAMILIES) {
      if (isParentPath(from, family) && family.isChild(to)) return 'forward';
    }
  }

  return 'none';
}

export const routeTransitionNavigateState = {
  forward: { routeTransition: 'forward' as const },
  back: { routeTransition: 'back' as const },
  lateral: { routeTransition: 'lateral' as const },
};

export function routeTransitionStateForNavigation(
  from: string,
  to: string
): RouteTransitionLocationState | undefined {
  const kind = classifyRouteTransition(from, to, 'PUSH', null);
  if (kind === 'none') return undefined;
  return { routeTransition: kind };
}
